import { useQuery as useRqQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo } from 'react';
import type { AnySchema } from 'yup';

import useRefreshAccessToken from '@/core/hooks/auth/useRefreshAccessToken';
import useRenewAccessToken from '@/core/hooks/auth/useRenewAccessToken';
import { useAuthContext } from '@/core/lib/auth/auth.context';
import renewOrRefresh from '@/core/lib/auth/renewOrRefresh';
import { useEnvContext } from '@/core/lib/env/env.context';
import type { APIDefinition, APIError } from '@/core/lib/fetch';
import { api, ApiMethods, ErrorKind } from '@/core/lib/fetch';
import { ApiVersions } from '@/core/lib/fetch/fetch';
import { useQueryContext } from '@/core/lib/query/query.context';

type RetryValue<TError> = boolean | number | ShouldRetryFunction<TError>;
type ShouldRetryFunction<TError = APIError> = (failureCount: number, error: TError) => boolean;

export interface UseQueryParams<TQueryFnData, TData = TQueryFnData> {
  queryKey: readonly string[];
  version?: ApiVersions;
  apiDefinition?: APIDefinition;
  schema: AnySchema;
  enabled?: boolean;
  shouldBeLoggedIn?: boolean;
  select?: (data: TQueryFnData) => TData;
  retry?: RetryValue<APIError>;
}

const useQuery = <TQueryFnData, TData = TQueryFnData>({ apiDefinition, queryKey, version, enabled = true, schema, select, shouldBeLoggedIn = false, retry }: UseQueryParams<TQueryFnData, TData>) => {
  const { accessToken, accessExpiresAt, refreshToken, refreshExpiresAt, isReady, logout } = useAuthContext();
  const { DONNONS_API_URL } = useEnvContext();
  const { contextRetry } = useQueryContext();

  const renew = useRenewAccessToken();
  const refresh = useRefreshAccessToken();

  const def = apiDefinition ?? { url: `${DONNONS_API_URL}${version ?? ApiVersions.V1}${queryKey.join('')}`, method: ApiMethods.GET };

  const queryFn = async () => {
    const { shouldRenew, shouldRefresh } = renewOrRefresh({ accessExpiresAt, refreshExpiresAt });

    if (accessToken) {
      if (shouldRenew) {
        renew.mutate();
        return api<TQueryFnData>(def, accessToken);
      }

      if (!shouldRenew && !shouldRefresh) {
        return api<TQueryFnData>(def, accessToken);
      }

      if (shouldRefresh && refreshToken) {
        const login = await refresh.mutateAsync({ token: refreshToken });
        return api<TQueryFnData>(def, login.access_token);
      }
    }

    return api<TQueryFnData>(def);
  };

  const computedEnabled = useMemo(() => {
    if (shouldBeLoggedIn && !accessToken) {
      return false;
    }

    return isReady && enabled;
  }, [shouldBeLoggedIn, accessToken, isReady, enabled]);

  const query = useRqQuery<TQueryFnData, APIError, TData>({
    queryKey,
    queryFn,
    enabled: computedEnabled,
    retry: retry ?? contextRetry,
    select: useCallback(
      (data: TQueryFnData): TData => {
        const transform = select || ((qData: TQueryFnData) => qData as unknown as TData);

        try {
          schema.validateSync(data);
          return transform(data);
        } catch (err) {
          console.warn(`[Schema Validation Error] API : ${def.method} ${def.url}`, { data, err });
          return transform(data);
        }
      },
      [select],
    ),
  });

  useEffect(() => {
    const logoutIfUnauthorized = async () => {
      if (query.isError && query.error) {
        if (query.error.kind === ErrorKind.Unauthorized) {
          await logout();
        }
      }
    };

    logoutIfUnauthorized();
  }, [query.isError, query.error]);

  return query;
};

export default useQuery;
